import { useMutation } from "@tanstack/react-query";
import { exportBatch, type Batch, type BatchExportFormat } from "@/api/client";
import { saveBlob } from "@/components/download";
import { ErrorBanner, ToolbarButton } from "@/components/ui";

const EXPORTS: { format: BatchExportFormat; label: string; suffix: string }[] = [
  { format: "recipients", label: "Recipients CSV", suffix: "recipients.csv" },
  { format: "seating", label: "Seating CSV", suffix: "seating.csv" },
  { format: "pdf-zip", label: "PDF bundle (ZIP)", suffix: "certificates.zip" },
];

/** Batch names are free text; keep file names to characters every OS accepts. */
export function exportBaseName(batch: Pick<Batch, "name" | "created_at">) {
  const name = (batch.name ?? "").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
  return name || `batch-${(batch.created_at ?? "").slice(0, 10) || "untitled"}`;
}

export function BatchExportPanel({ batch }: { batch: Batch }) {
  const download = useMutation({
    mutationFn: async (item: (typeof EXPORTS)[number]) => {
      const file = await exportBatch(batch.id, item.format);
      saveBlob(file, `${exportBaseName(batch)}-${item.suffix}`);
    },
  });

  return (
    <section className="space-y-3 rounded-md border border-zinc-200 p-3">
      <div>
        <h3 className="text-sm font-semibold">Export</h3>
        <p className="mt-1 text-xs text-zinc-500">Recipient and seating lists, or every generated certificate's PDF.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {EXPORTS.map((item) => (
          <ToolbarButton key={item.format} disabled={download.isPending} onClick={() => download.mutate(item)}>
            {item.label}
          </ToolbarButton>
        ))}
      </div>
      {download.isPending && download.variables ? (
        <p role="status" className="text-xs text-zinc-500">
          Preparing the {download.variables.label}...
        </p>
      ) : null}
      <ErrorBanner error={download.error} />
    </section>
  );
}
